// SideRail — left navigation column.
//
// Mobile: collapsible drawer (full-width 256px when open, hidden when
// closed). Triggered by the hamburger button in the map top bar.
//
// Desktop: permanent 64px icon rail (no toggle, no drawer). Tooltips on
// hover. Mirrors the ForeFlight desktop pattern.

import { NavLink } from 'react-router-dom';
import { useUiStore } from '../stores/uiStore';
import { Icon } from './Icon';
import { useIsDesktop } from './useBreakpoint';
import { useAccountContextStore } from '../stores/accountContextStore';
import AccountContextSwitcher from '../components/AccountContextSwitcher';
import PublicationsBadge from '../map/PublicationsBadge';
import CellularBadge from '../map/CellularBadge';

interface NavItem {
  to: string;
  label: string;
  icon: 'map' | 'airport' | 'plate' | 'flights' | 'book' | 'plane' | 'incidents' | 'crew' | 'frat' | 'track' | 'documents' | 'gear';
  /** When true, hidden in personal mode (org-scoped feature). */
  orgOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: '/map', label: 'Map', icon: 'map' },
  { to: '/airports', label: 'Airports', icon: 'airport' },
  { to: '/plates', label: 'Plates', icon: 'plate' },
  { to: '/flights', label: 'Flights', icon: 'flights' },
  { to: '/logbook', label: 'Logbook', icon: 'book' },
  { to: '/aircraft', label: 'Aircraft', icon: 'plane' },
  { to: '/incidents', label: 'Incidents', icon: 'incidents', orgOnly: true },
  { to: '/crew', label: 'Crew', icon: 'crew', orgOnly: true },
  { to: '/frat', label: 'FRAT', icon: 'frat', orgOnly: true },
  { to: '/tracks', label: 'Track Logs', icon: 'track' },
  { to: '/documents', label: 'Documents', icon: 'documents' },
  { to: '/settings', label: 'Settings', icon: 'gear' },
];

export default function SideRail() {
  const open = useUiStore((s) => s.railOpen);
  const toggle = useUiStore((s) => s.toggleRail);
  const isDesktop = useIsDesktop();
  const isOrgMode = useAccountContextStore((s) => s.ctx.kind === 'organizational');
  // Filter org-only items out when the user is in personal context.
  const visibleNav = NAV.filter((n) => !n.orgOnly || isOrgMode);

  // Desktop: permanent 64px rail, no toggle, no drawer.
  if (isDesktop) {
    return (
      <aside
        className="relative h-full w-16 bg-cockpit-800 border-r border-cockpit-600 flex flex-col shadow-rail"
        style={{ paddingTop: 'var(--safe-top)' }}
      >
        <div className="flex items-center justify-center h-12 border-b border-cockpit-700">
          {/* Firefly mark replaces the placeholder 🔥 emoji. PNG
              source (icon-192) is the authoritative artwork; SVG would
              re-fetch the same PNG via /icon.svg's <image> tag, costing
              an extra request for nothing. */}
          <img
            src="/icon-192.png"
            alt="Fyrefly"
            title="Fyrefly"
            width={32}
            height={32}
            className="h-8 w-8 rounded-lg"
          />
        </div>
        {/* Personal / Organization switcher — sits directly below the logo. */}
        <div className="px-1.5 py-2 border-b border-cockpit-700 flex justify-center">
          <AccountContextSwitcher variant="rail" />
        </div>
        <nav className="flex-1 overflow-y-auto scroll-touch py-2">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              title={item.label}
              className={({ isActive }) =>
                [
                  'flex items-center justify-center h-12 mx-2 rounded-lg',
                  'hover:bg-cockpit-700 transition-colors',
                  isActive ? 'bg-cockpit-700 text-ember-400' : 'text-cockpit-200',
                ].join(' ')
              }
              end
            >
              <Icon name={item.icon} />
            </NavLink>
          ))}
        </nav>
        {/* Footer block: chart-currency alert (silent when current)
            sits above the version stamp. Boss req 2026-05-13: 'the
            chart currency alert should be moved there too, maybe at
            the bottom of the menu there'. PublicationsBadge renders
            null when nothing is expired/expiring so the rail stays
            clean in the happy path. */}
        <div className="px-1.5 py-2 border-t border-cockpit-700 flex flex-col items-center gap-1">
          <CellularBadge />
          <PublicationsBadge />
        </div>
        <div className="px-2 py-2 border-t border-cockpit-700 text-[10px] text-cockpit-400 text-center font-mono">
          v0.1
        </div>
      </aside>
    );
  }

  // Mobile drawer (existing behavior).
  return (
    <aside
      className={[
        'absolute z-40 h-full bg-cockpit-800 border-r border-cockpit-600 shadow-rail',
        'flex flex-col transition-[width] duration-200',
        open ? 'w-64' : 'w-0',
        'overflow-hidden',
      ].join(' ')}
      style={{ paddingTop: 'var(--safe-top)' }}
    >
      <div className="flex items-center gap-2 px-3 py-3 border-b border-cockpit-700">
        <button
          onClick={() => toggle()}
          className="h-10 w-10 rounded-lg flex items-center justify-center hover:bg-cockpit-700 active:bg-cockpit-600 shrink-0"
          aria-label="Toggle menu"
        >
          <Icon name="menu" />
        </button>
        {open && (
          <div className="flex items-center gap-2">
            <img
              src="/icon-192.png"
              alt="Fyrefly"
              width={28}
              height={28}
              className="h-7 w-7 rounded-md"
            />
            <span className="font-semibold tracking-wide text-cockpit-100">Fyrefly</span>
          </div>
        )}
      </div>

      {/* Personal / Organization switcher — mirrors the desktop rail
          placement (right below the logo / title). Boss req 2026-05-13:
          'the personal/organization selector button on mobile should
          be moved to the menu bar in the upper left somewhere like on
          desktop'. Only renders when the drawer is open AND the user
          has at least one org (the switcher hides itself when there's
          nothing to switch to). */}
      {open && (
        <div className="px-3 py-2 border-b border-cockpit-700">
          <AccountContextSwitcher />
        </div>
      )}

      <nav className="flex-1 overflow-y-auto scroll-touch py-2">
        {visibleNav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => toggle(false)}
            className={({ isActive }) =>
              [
                'flex items-center gap-3 px-3 py-3 mx-2 rounded-lg text-sm',
                'hover:bg-cockpit-700 active:bg-cockpit-600 transition-colors',
                isActive ? 'bg-cockpit-700 text-ember-400' : 'text-cockpit-200',
              ].join(' ')
            }
            end
          >
            <span className="h-6 w-6 flex items-center justify-center shrink-0">
              <Icon name={item.icon} />
            </span>
            {open && <span className="truncate">{item.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Chart-currency alert near the drawer bottom. Boss req
          2026-05-13: 'the chart currency alert should be moved there
          too, maybe at the bottom of the menu there'. PublicationsBadge
          silently returns null in the 'current' state so this row
          collapses to nothing in the happy path. */}
      {open && (
        <div className="px-3 py-2 border-t border-cockpit-700 flex flex-col gap-1.5 items-start">
          <CellularBadge />
          <PublicationsBadge />
        </div>
      )}

      <div className="px-3 py-3 border-t border-cockpit-700 text-xs text-cockpit-300">
        {open ? (
          <div>
            <div className="font-mono text-cockpit-200">v0.1.0</div>
            <div>Pre-alpha · supplemental only</div>
          </div>
        ) : (
          <div className="text-center font-mono">v0.1</div>
        )}
      </div>
    </aside>
  );
}
